<template>
  <div class="flex flex-col gap-1">
    <label class="font-semibold" :for="name">
      {{ label }}<span v-if="required" class="text-red-600">*</span>
    </label>
    <Multiselect
      v-model="localValue"
      mode="tags"
      :options="transformedOptions"
      @update:modelValue="handleInput"
    />
    <p class="text-red-500" v-show="errorMessage">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, computed, onMounted } from 'vue';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import Multiselect from '@vueform/multiselect';
import { useFilterStore } from '@/store/filterStore.js';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  property: {
    type: Number,
    default: undefined,
  },
  defaultValues: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const localValue = ref([]);
const filterStore = useFilterStore();

onMounted(() => {
  if (props.property) {
    // загрузить полный список опций централизованно
    filterStore.loadAllSelectValues(props.property);
  }
});

// Validation schema
const schema = computed(() => {
  return yup
    .array()
    .min(1, 'Choose at least one')
    .required(props.required ? t('warning.REQUIRED_MESSAGE') : false);
});

// Setup vee-validate field
const { value, errorMessage, handleChange, validate } = useField(props.name, schema, {
  initialValue: props.defaultValues.filter(item => !item.parent).map(item => item.id) || [],
});

// Sync localValue with vee-validate's value
watch(
  value,
  newVal => {
    localValue.value = newVal || [];
  },
  { immediate: true },
);

// Handle input changes
const handleInput = newValue => {
  localValue.value = newValue;
  handleChange(newValue);
};

// Transform options for Multiselect
const transformedOptions = computed(() => {
  const items = (props.property && filterStore.optionsByProperty?.[props.property])?.length
    ? filterStore.optionsByProperty[props.property]
    : props.options;

  return (items || []).map(item => ({
    value: item.id,
    label: item.name,
  }));
});

// Expose validateField method to parent component
const validateField = async () => {
  const { valid } = await validate();
  return {
    value: localValue.value?.length === 0 ? undefined : localValue.value,
    valid,
    mode: 'multi',
    property: props.property,
  };
};

defineExpose({
  validateField,
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
